/**
 * useLessonBatchExecution — manages per-item execution state for fan-out lesson stages.
 * Mirrors useBatchExecution from the article pipeline but uses lesson service methods.
 */

import { useCallback, useRef, useState } from 'react';
import { useLessonContext } from '../LessonContext';
import { jobsService } from '../../../services/jobsService';
import { courseRunsService } from '../../../services/courseRunsService';

interface ItemStatus {
  status: 'pending' | 'running' | 'succeeded' | 'failed';
  error?: string;
}

export function useLessonBatchExecution(
  { stageId, getExtraParams }: { stageId: string; getExtraParams?: () => { ttsProvider?: string; ttsModel?: string; voice?: string } },
) {
  const { lessonRun, refreshRun, refreshDocument } = useLessonContext();

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [batchStatus, setBatchStatus] = useState<Record<string, ItemStatus>>({});
  const [runningAll, setRunningAll] = useState(false);
  const [runningSingle, setRunningSingle] = useState(false);
  const cancelRef = useRef(false);

  const runItem = useCallback(async (itemId: string) => {
    if (!lessonRun?.run_id) return;
    setBatchStatus((prev) => ({ ...prev, [itemId]: { status: 'running' } }));
    try {
      const { job_id } = await courseRunsService.executeLessonStage(
        lessonRun.run_id, stageId, itemId, getExtraParams?.(),
      );
      const finalJob = await jobsService.waitForCompletion(job_id, {});
      if (finalJob.status === 'failed') {
        throw new Error((finalJob as any).error || 'Stage execution failed');
      }
      setBatchStatus((prev) => ({ ...prev, [itemId]: { status: 'succeeded' } }));
    } catch (e: unknown) {
      setBatchStatus((prev) => ({
        ...prev,
        [itemId]: { status: 'failed', error: e instanceof Error ? e.message : 'Failed' },
      }));
    }
  }, [lessonRun?.run_id, stageId, getExtraParams]);

  const runSingle = useCallback(async (itemId: string) => {
    setRunningSingle(true);
    try {
      await runItem(itemId);
      await refreshRun();
      await refreshDocument();
    } finally {
      setRunningSingle(false);
    }
  }, [runItem, refreshRun, refreshDocument]);

  const runAll = useCallback(async (itemIds: string[]) => {
    if (!lessonRun?.run_id) return;
    setRunningAll(true);
    cancelRef.current = false;

    for (const itemId of itemIds) {
      if (cancelRef.current) break;
      await runItem(itemId);
      // Refresh document after each item so generated content appears immediately
      await refreshDocument();
    }

    await refreshRun();
    setRunningAll(false);
  }, [lessonRun?.run_id, runItem, refreshRun, refreshDocument]);

  const cancelBatch = useCallback(() => {
    cancelRef.current = true;
  }, []);

  return {
    selectedId,
    setSelectedId,
    batchStatus,
    runningAll,
    runningSingle,
    runSingle,
    runAll,
    cancelBatch,
  };
}
